/** Strings for the resource screens (S6): index, detail, form and per-kind fields. */
export const resourcesCopy = {
  add: 'Add resource',
  filterGroup: 'Filter resources',
  retry: 'Retry',
  clearFilters: 'Clear filters',
  loadFailed: 'Could not load resources.',
  groups: { topic: 'This topic', workspace: 'From the workspace' },
  /** Review-state badge words (see `reviewFlag`). */
  flag: { approve: 'approve', answer: 'answer' },
  facets: {
    all: 'All',
    connections: 'Connections',
    agentCreated: 'Agent-created',
    needsReview: 'Needs review',
  },
  list: {
    title: 'Resources',
    description: 'Workspace documents and connections',
    crumb: 'Resources',
    workspaceCrumb: 'Workspace',
    topicEmpty: 'No resources in this topic yet',
    workspaceEmpty: 'No resources in this workspace yet',
    /** Names the active facet. */
    filteredEmpty: (
      facet: 'all' | 'connections' | 'agent_created' | 'needs_review',
    ) =>
      ({
        all: 'No resources.',
        connections: 'No connections.',
        agent_created: 'No agent-created resources.',
        needs_review: 'No resources need review.',
      })[facet],
    workspaceScoped: 'Workspace-scoped',
    groupIn: (name: string) => `In ${name}`,
    agentFallback: 'agent',
    someoneFallback: 'someone',
    workspaceTag: 'workspace',
  },
  detail: {
    title: 'Resource',
    loadFailed: 'Could not load this resource.',
    noOwningTopic: 'Failed to move resource — it has no owning topic.',
    moved: 'Resource moved',
    moveFailed: 'Failed to move resource — try again.',
    moveToTopic: 'Move to topic',
    moveToWorkspace: 'Move to workspace',
    edit: 'Edit',
    visibilityThread: 'Visible in its thread only',
    visibilityTopic: 'Visible to every thread in its topic',
    visibilityWorkspace: 'Visible in every topic',
    byAgent: (name: string) => `by agent ${name}`,
    by: (name: string) => `by ${name}`,
    created: (time: string) => `created ${time}`,
    updated: (time: string) => `updated ${time}`,
    promotedFrom: (scope: string, time?: string) =>
      time ? `promoted from ${scope} · ${time}` : `promoted from ${scope}`,
  },
  view: {
    noteEmpty: 'This note is empty.',
    answer: 'Answer',
    todoEmpty: 'No items yet.',
    fact: {
      url: 'URL',
      endpoint: 'Endpoint',
      remote: 'Remote',
      branch: 'Branch',
      toolsets: 'Toolsets',
      path: 'Path',
      event: 'Event',
      note: 'Note',
      schedule: 'Schedule',
      task: 'Task',
      account: 'Account',
      status: 'Status',
      repositories: 'Repositories',
    },
    allRepositories: 'every repository the installation can reach',
    defaultValue: 'default',
    any: 'any',
    notSet: 'not set',
    oneShot: 'one-shot',
    disabled: 'disabled',
  },
  form: {
    kind: 'Kind',
    loadFailed: 'Could not load the form.',
    preview: 'Preview',
    previewEmpty: 'Nothing to preview yet',
    name: 'Name',
    save: 'Save changes',
    cancel: 'Cancel',
    deleteResource: 'Delete resource',
    deleteTitle: 'Delete resource',
    deleteConfirm: 'Delete',
    deleteDescription: (name?: string) =>
      name
        ? `Permanently deletes "${name}".`
        : 'Permanently deletes this resource.',
    deleteFailed: (reason: string) => `Failed to delete resource — ${reason}`,
    requestFailed: 'Request failed — try again.',
    crumbEdit: 'Edit',
    crumbAdd: 'Add',
    editTitle: 'Edit resource',
    addDescription: 'Give agents something to read, query or call.',
    optional: '(optional)',
    leaveBlankToKeep: '(leave blank to keep)',
  },
  forms: {
    cron: {
      fires: 'Fires',
      recurring: 'On a schedule',
      oneShot: 'Once',
      fireAt: 'Fire at',
      schedule: 'Schedule',
      interval: 'At an interval',
      daily: 'Daily',
      expression: 'Cron expression',
      every: 'Every',
      unit: 'Unit',
      minutes: 'Minutes',
      hours: 'Hours',
      timeOfDay: 'Time of day',
      expressionHint: '(min hour day month weekday)',
      expressionPlaceholder: '*/10 * * * *',
      task: 'Task',
      eventType: 'Event type',
      eventTypePlaceholder: 'cron.fired',
      activeHours: 'Active hours',
      activeHoursPlaceholder: '09:00-17:00',
      timezone: 'Timezone',
      timezonePlaceholder: 'America/New_York',
      enabled: 'Enabled',
    },
    fallback: {
      data: 'Data (JSON)',
      invalidJson: (reason: string) => `Data is not valid JSON — ${reason}`,
    },
    github: {
      authMode: 'Auth mode',
      authInstall: 'Install the GitHub App',
      authOwnApp: 'Create your own GitHub App',
      authApp: 'GitHub App credentials',
      authPat: 'Personal access token',
      organization: 'Organization',
      organizationHelp: 'Blank creates the App on your personal account.',
      organizationPlaceholder: 'my-org',
      appId: 'App ID',
      appIdPlaceholder: '123456',
      installationId: 'Installation ID',
      installationHint: '(optional)',
      installationPlaceholder: '78901234',
      privateKeyKeep: 'App private key',
      privateKey: 'App private key (PEM)',
      privateKeyPlaceholder:
        '-----BEGIN RSA PRIVATE KEY-----\n…\n-----END RSA PRIVATE KEY-----',
      redeliver: 'Redeliver failed webhooks',
      allowApprove: 'Allow the agent to approve pull requests',
      tokenKeep: 'Personal access token',
      token: 'Personal access token',
      tokenPlaceholder: 'github_pat_… or ghp_…',
      toolsets: 'Toolsets',
      toolsetsHint: '(comma-separated)',
      readOnly: 'Read-only',
      enterpriseHost: 'GitHub Enterprise host',
      webUrl: 'GitHub web URL',
      hostHelp: 'Blank uses github.com.',
      hostPlaceholder: 'https://github.example.com',
      central: 'Credentials are managed by the central GitHub App.',
      appSlug: 'App slug',
      appSlugHelp: "The name in the App's URL, github.com/apps/<slug>.",
      appSlugPlaceholder: 'acme-bot',
      repositories: 'Repositories',
      repositoriesHelp:
        'Comma-separated; blank allows every repository the installation can reach.',
      repositoriesPlaceholder: 'api, acme/web',
      afterSave:
        "After saving, copy the connection's webhook URL and secret into the App's settings on GitHub.",
      needsWorkspace:
        "GitHub setup needs a workspace — add this resource from the workspace's resources page.",
    },
    gitRepo: {
      remote: 'Remote URL',
      remotePlaceholder: 'https://github.com/org/repo.git',
      branch: 'Branch',
      branchPlaceholder: 'main',
      token: 'Token',
      tokenPlaceholder: 'ghp_…',
    },
    note: {
      content: 'Content (Markdown)',
    },
    plan: {
      plan: 'Plan (Markdown)',
      reviewerFeedback: (text: string) => `Reviewer feedback: “${text}”`,
      submitted: (time: string) => `submitted ${time}`,
      decided: (status: string, time: string, by?: string) =>
        by ? `${status} ${time} by ${by}` : `${status} ${time}`,
    },
    question: {
      question: 'Question',
      options: 'Options',
      optionsHelp:
        'One per line, as “label” or “label | value”. Empty means free-text answer.',
      optionsPlaceholder: 'Ship it\nHold | hold-off',
      defaultStatus: 'submitted',
      asked: (time: string) => `asked ${time}`,
      answer: 'answer:',
      /** Who answered and when, after the answer text; empty when neither. */
      answeredBy: (by?: string, time?: string) => {
        if (by) return ` (${time ? `${by}, ${time}` : by})`
        return time ? ` ${time}` : ''
      },
    },
    sqlite: {
      path: 'Path',
      pathPlaceholder: '/absolute/path/to/file',
    },
    url: {
      url: 'URL',
      placeholder: 'https://…',
    },
    eventRoute: {
      source: 'Source connection',
      fixedHelp: 'Cannot be changed — delete the route and create a new one.',
      chooseSource: 'Choose a source',
      target: 'Target topic',
      chooseTopic: 'Choose a topic',
      selfTarget: 'Events go to this topic.',
      when: 'When',
      whenPlaceholder: '{event_type}',
      equals: 'Equals',
      equalsHelp: 'Blank routes every event; a trailing .* is a wildcard.',
      equalsPlaceholder: 'github.pull_request.*',
      agentMessage: 'Agent message',
      agentMessageHint: '(optional)',
      agentMessagePlaceholder:
        'PR #{body.number} {body.action} by {body.sender.login}',
      typeOverride: 'Event type override',
      typeOverrideHint: '(optional)',
      typeOverridePlaceholder: 'review.requested',
      sourceOption: (name: string, kind: string) => `${name} (${kind})`,
      paused: 'Paused',
      fallback: 'Fallback for events no other route matched',
      behaviour: 'Behaviour',
      resolveOnClosed: 'Mark the thread done when the item closes',
      supersedeOnUpdated: 'Start over when the item changes',
      coalesce: 'Wait for activity to settle (seconds)',
      coalesceHelp:
        'A burst of events about one item gets one reply; 0 replies to each.',
      maxEvents: 'Reply limit per item',
      maxEventsHelp:
        'Later events are still recorded; reopening the item resets the count.',
    },
    webhook: {
      eventType: 'Event type',
      eventTypePlaceholder: 'webhook.received',
      secret: 'Signing secret',
      eventTypeHelp: 'Applies when no rule sets one.',
      secretHelp: 'Without one, every request is rejected.',
      signatureHeader: 'Signature header',
      signatureHeaderPlaceholder: 'x-hub-signature-256',
      signaturePrefix: 'Signature prefix',
      signaturePrefixHelp: 'Clear it for a bare digest.',
      description: 'Description',
    },
    predicate: {
      when: 'When',
      whenPlaceholder: '{event_type}',
      whenHelp:
        'Paths: {event_type}, {correlation_key}, {entity}, {facts.…}, {body.…}, {headers.…}, {payload.body.…}',
      comparison: 'Comparison',
      comparisons: {
        equals: 'equals',
        not_equals: 'does not equal',
        in: 'is one of',
        in_ci: 'is one of (any case)',
        contains: 'contains',
        exists: 'is present',
      },
      value: 'Value',
      values: 'Values',
      valueHelp: {
        equals: 'Blank matches every event; a trailing .* is a wildcard.',
        not_equals: 'A trailing .* is a wildcard.',
        in: 'One per line or comma-separated; a trailing .* is a wildcard.',
        in_ci: 'Case does not matter.',
        contains: 'Case does not matter.',
      },
      valuePlaceholder: 'deploy.*',
      valuesPlaceholder: 'deploy.failed\ndeploy.succeeded',
      andAlso: 'And also',
      andAlsoHelp: 'Every condition must hold.',
      kept: (keys: string) => `Kept as stored: ${keys}`,
      rows: {
        path: (label: string, n: number) => `${label} ${n}: path`,
        comparison: (label: string, n: number) => `${label} ${n}: comparison`,
        value: (label: string, n: number) => `${label} ${n}: value`,
        remove: (label: string, n: number) => `Remove ${label} ${n}`,
        add: 'Add condition',
        pathPlaceholder: '{body.status}',
        listPlaceholder: 'a, b, c',
        valuePlaceholder: 'failed',
      },
    },
    workflow: {
      agent: 'Agent',
      agentHelp: 'Saving adds the agent to the topic.',
      chooseAgent: 'Choose an agent',
      instructions: 'Instructions',
      instructionsHelp: 'Event values can be referenced like {body.before}.',
      instructionsPlaceholder:
        'Look into why this deploy failed and say what to do next.',
      paused: 'Paused',
    },
    recipe: {
      startFrom: 'Start from',
      blank: 'Blank manifest',
      manifest: 'Manifest (JSON)',
      empty: 'Enter a manifest.',
      invalidJson: (reason: string) => `Manifest is not valid JSON — ${reason}`,
    },
    normalize: {
      rules: 'Rules',
      help: 'The first matching rule applies. Templates read the request as {body.…} and {headers[name]}.',
      rule: (n: number) => `Rule ${n}`,
      managedBy: (profile: string) => `managed by ${profile}`,
      keptAsStored: 'kept as stored',
      remove: 'Remove',
      removeRule: (n: number) => `Remove rule ${n}`,
      conditions: 'Conditions',
      conditionsHelp:
        'Every condition must hold. Without any, every request matches.',
      drop: 'Ignore these requests',
      eventType: 'Event type',
      eventTypePlaceholder: 'deploy.{body.status}',
      item: 'Item',
      itemHelp: 'Requests about the same item share a thread.',
      itemPlaceholder: 'deploy:{body.id}',
      entity: 'What happened to the item',
      entities: {
        none: 'Nothing in particular',
        closed: 'Closed or finished',
        updated: 'Changed',
        reopened: 'Reopened',
      },
      message: 'Message for the agent',
      messagePlaceholder: 'Deploy {body.id} of {body.service}: {body.status}',
      dedup: 'Duplicate key',
      dedupHelp: 'Repeats of the same key are handled once.',
      dedupPlaceholder: '{headers[x-delivery]}',
      add: 'Add rule',
    },
  },
  todo: {
    remove: (item: string) => `Remove ${item}`,
    done: 'done',
    items: 'Items',
    empty: 'No items yet.',
    newItem: 'Item',
    add: 'Add',
    addItem: 'Add item',
    addFailed: (reason: string) => `Failed to add item — ${reason}`,
    removeFailed: (reason: string) => `Failed to remove item — ${reason}`,
    updateFailed: (reason: string) => `Failed to update item — ${reason}`,
  },
} as const
